import React, { createContext, useContext, useState, ReactNode } from "react";
import toast from "react-hot-toast";

import { useAuth } from "./AuthContext";

import { OrderData, PickupPoint, FirebaseOrder } from "@/models";
import { FirebaseOrderService } from "@/services";
import { resolveShippingMethodConfigId } from "@/shared/utils/shippingMethodConfigId";

type OrderContextType = {
  orderData: OrderData;
  setOrderData: React.Dispatch<React.SetStateAction<OrderData>>;
  selectedPickupPoint: PickupPoint | null;
  setSelectedPickupPoint: React.Dispatch<
    React.SetStateAction<PickupPoint | null>
  >;
  paymentMethod: "stripe" | "paypal" | null;
  setPaymentMethod: React.Dispatch<
    React.SetStateAction<"stripe" | "paypal" | null>
  >;
  isOrderComplete: boolean;
  setIsOrderComplete: React.Dispatch<React.SetStateAction<boolean>>;
  clearOrder: () => void;

  currentOrderId: string | null;
  currentStep: 1 | 2 | 3 | "complete";
  setCurrentStep: React.Dispatch<React.SetStateAction<1 | 2 | 3 | "complete">>;
  firebaseOrder: FirebaseOrder | null;
  isLoading: boolean;
  isMergedOrder: boolean;
  setIsMergedOrder: React.Dispatch<React.SetStateAction<boolean>>;

  // Étape 1 : création / chargement de la commande (produits + infos de base)
  saveOrder: (
    formData?: OrderData,
  ) => Promise<{ isMerged: boolean; orderId?: string | null }>;
  saveDeliveryInfo: (deliveryData?: {
    deliveryMethod: string | null;
    shippingCost?: number;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    pickupPoint?: PickupPoint | null;
  }) => Promise<void>;
  // Étape 2 : préparation du paiement (méthode de paiement + commission)
  savePaymentInfo: (paymentInfo?: {
    commissionRate?: number;
    fixedCommission?: number;
    currency?: string;
  }) => Promise<void>;
  completeOrder: () => Promise<void>;
  loadOrder: (orderId: string) => Promise<void>;
};

const defaultOrderData: OrderData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  tiktokUsername: "",
  productReference: "",
  productModel: "",
  amount: "",
  address: "",
  city: "",
  postalCode: "",
  country: "FR",
  deliveryMethod: null,
  shippingCost: 0,
};

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuth();
  const [orderData, setOrderData] = useState<OrderData>(defaultOrderData);
  const [selectedPickupPoint, setSelectedPickupPoint] =
    useState<PickupPoint | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<
    "stripe" | "paypal" | null
  >(null);
  const [isOrderComplete, setIsOrderComplete] = useState(false);

  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | "complete">(1);
  const [firebaseOrder, setFirebaseOrder] = useState<FirebaseOrder | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isMergedOrder, setIsMergedOrder] = useState(false);

  const clearOrder = () => {
    setOrderData(defaultOrderData);
    setSelectedPickupPoint(null);
    setPaymentMethod(null);
    setIsOrderComplete(false);
    setCurrentOrderId(null);
    setCurrentStep(1);
    setFirebaseOrder(null);
    setIsMergedOrder(false);
  };

  const saveOrder = async (formData?: OrderData) => {
    setIsLoading(true);
    try {
      console.log("💾 saveOrder called...");

      if (currentOrderId) {
        console.log(
          "⚠️ Order already exists, checking if should skip to payment...",
        );

        // Load existing order to check delivery payment status
        try {
          const existingOrder =
            await FirebaseOrderService.getOrder(currentOrderId);
          if (
            existingOrder &&
            existingOrder.deliveryInfo.paymentStatus === "paid"
          ) {
            console.log(
              "🔄 Delivery already paid, adding new product to existing order...",
            );

            // Use provided form data or existing orderData
            const dataToSave = formData || orderData;

            // Create the new product to add
            const newProduct = {
              reference: dataToSave.productReference,
              model: dataToSave.productModel || '',
              amount: dataToSave.amount, // ← Le montant 100€
              tiktokUsername: dataToSave.tiktokUsername,
              paymentInfo: {
                status: 'pending' as const
              },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            console.log("📦 Adding new product with amount:", newProduct.amount);

            // Add the new product to Firebase
            await FirebaseOrderService.addProductToOrder(currentOrderId, newProduct);

            // Reload the order to get updated products list
            await new Promise(resolve => setTimeout(resolve, 300));
            const updatedOrder = await FirebaseOrderService.getOrder(currentOrderId);

            if (updatedOrder) {
              console.log("📦 Updated products list:", updatedOrder.products.map(p => ({
                model: p.model,
                amount: p.amount,
              })));

              // Update context with the new products list
              setOrderData({
                ...orderData,
                ...dataToSave,
                products: updatedOrder.products,
              });
            }

            setIsMergedOrder(true);
            setCurrentStep(3);
            return { isMerged: true, orderId: currentOrderId };
          } else {
            console.log("💰 Delivery not paid yet, proceeding normally");
            return { isMerged: false, orderId: currentOrderId };
          }
        } catch (_error) {
          console.error("❌ Error checking existing order:", _error);
          return { isMerged: false, orderId: currentOrderId };
        }
      }

      // Use provided form data or existing orderData
      const dataToSave = formData || orderData;

      // Use the authenticated user's email if available, otherwise use the form email
      const emailToUse = currentUser?.email || dataToSave.email;

      const finalOrderData = {
        ...dataToSave,
        email: emailToUse,
      };

      console.log("📤 Creating order with data:", finalOrderData);
      // Passer currentUser?.uid pour créer le customer avec firebaseUid si connecté
      const result = await FirebaseOrderService.createOrder(finalOrderData, currentUser?.uid);
      console.log("📋 Firebase result:", result);
      console.log("📋 Firebase result.orderId:", result.orderId);
      console.log("📋 Firebase result.isMerged:", result.isMerged);

      setCurrentOrderId(result.orderId);

      if (result.isMerged) {
        console.log("🔄 Order was merged, loading existing order data...");

        // Set the merged order flag
        setIsMergedOrder(true);

        // Load the complete existing order data when merging
        // Small delay to ensure Firebase has propagated the new product
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          const existingOrder = await FirebaseOrderService.getOrder(
            result.orderId,
          );
          if (existingOrder) {
            console.log("📋 Loaded existing order data:", existingOrder);
            console.log("📦 Products in merged order:", existingOrder.products.map(p => ({
              model: p.model,
              amount: p.amount,
            })));

            // Update order data with existing order info
            // The products array from Firebase should now include the new product
            setOrderData({
              ...orderData,
              ...finalOrderData, // Keep the new product info (amount, model, etc.)
              // Load delivery info from existing order
              address: existingOrder.deliveryInfo.address || "",
              city: existingOrder.deliveryInfo.city || "",
              postalCode: existingOrder.deliveryInfo.postalCode || "",
              /** Id config (nanoid MR / sendcloud_*) — pas seulement methodId numérique */
              deliveryMethod: resolveShippingMethodConfigId(
                existingOrder.deliveryInfo as Record<string, unknown>,
              ) as any,
              shippingCost: existingOrder.deliveryInfo.shippingCost || 0,
              products: existingOrder.products, // Contains all products including the new one
            });

            // Set the pickup point from existing order
            if (existingOrder.deliveryInfo.pickupPoint) {
              console.log("📍 Setting pickup point from existing order");
              setSelectedPickupPoint(existingOrder.deliveryInfo.pickupPoint);
            }
          }
        } catch (loadError) {
          console.error("❌ Error loading existing order:", loadError);
          // Continue anyway, the merge was successful
        }

        // Skip delivery step for merged orders, go directly to payment
        setCurrentStep(3);
        toast.success("Product added to your existing order for today!");
      } else {
        console.log("📦 New order created, setting step to 2");
        // New order, proceed to delivery step
        setCurrentStep(2);
        toast.success("Order information saved successfully!");

        // Update the context state with the form data if provided
        if (formData) {
          setOrderData({
            ...orderData,
            ...formData,
            email: emailToUse, // Ensure email is updated in context
          });
        }
      }

      console.log("✅ Returning result:", { isMerged: result.isMerged, orderId: result.orderId });
      return { isMerged: result.isMerged, orderId: result.orderId };
    } catch (_error) {
      console.error("❌ Error saving order step 1:", _error);
      toast.error("Failed to save order information");
      throw _error;
    } finally {
      setIsLoading(false);
    }
  };

  const saveDeliveryInfo = async (deliveryData?: {
    deliveryMethod: string | null;
    shippingCost?: number;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    pickupPoint?: PickupPoint | null;
    methodName?: string;
    methodDescription?: string;
    provider?: string; // Fournisseur de livraison (sendcloud, chronopost, etc.)
    methodId?: number; // ID générique de la méthode de livraison (provider-agnostic)
    estimatedHours?: number;
    minWeight?: number;
    maxWeight?: number;
    servicePointInput?: string;
    isHomeDelivery?: boolean;
  }) => {
    if (!currentOrderId) {
      toast.error("Missing order ID");
      return;
    }

    setIsLoading(true);
    try {
      const finalDeliveryData = deliveryData || {
        deliveryMethod: orderData.deliveryMethod,
        shippingCost: orderData.shippingCost,
        address: orderData.address,
        city: orderData.city,
        postalCode: orderData.postalCode,
        pickupPoint: selectedPickupPoint,
        methodName: orderData.methodName,
        methodDescription: orderData.methodDescription,
        provider: orderData.provider,
        methodId: orderData.methodId,
        estimatedHours: orderData.estimatedHours,
        minWeight: orderData.minWeight ? Number(orderData.minWeight) : undefined,
        maxWeight: orderData.maxWeight ? Number(orderData.maxWeight) : undefined,
        servicePointInput: orderData.servicePointInput,
        isHomeDelivery: orderData.isHomeDelivery ?? undefined,
      };

      await FirebaseOrderService.updateDeliveryInfo({
        orderId: currentOrderId,
        deliveryMethod: finalDeliveryData.deliveryMethod,
        shippingCost: finalDeliveryData.shippingCost,
        address: finalDeliveryData.address,
        city: finalDeliveryData.city,
        postalCode: finalDeliveryData.postalCode,
        country: finalDeliveryData.country,
        pickupPoint: finalDeliveryData.pickupPoint || undefined,
        methodId: finalDeliveryData.methodId,
        provider: finalDeliveryData.provider || 'sendcloud', // Par défaut sendcloud pour la compatibilité
        weight: undefined,
        methodName: finalDeliveryData.methodName,
        methodDescription: finalDeliveryData.methodDescription,
        estimatedHours: finalDeliveryData.estimatedHours,
        minWeight: finalDeliveryData.minWeight,
        maxWeight: finalDeliveryData.maxWeight,
        servicePointInput: finalDeliveryData.servicePointInput,
        isHomeDelivery: finalDeliveryData.isHomeDelivery
      });

      console.log("Delivery information saved successfully");
      toast.success("Delivery information saved successfully!");
    } catch (_error) {
      console.error("Error saving delivery info:", _error);
      toast.error("Failed to save delivery information");
      throw _error;
    } finally {
      setIsLoading(false);
    }
  };

  const savePaymentInfo = async (paymentInfo?: {
    commissionRate?: number;
    fixedCommission?: number;
    currency?: string;
  }) => {
    if (!currentOrderId) {
      toast.error("Missing order ID");
      return;
    }

    setIsLoading(true);
    try {
      // Find the product index that needs payment (most recent pending product)
      let productIndex = 0; // Default to first product
      if (orderData.products && orderData.products.length > 0) {
        // Find the last product with pending payment status
        for (let i = orderData.products.length - 1; i >= 0; i--) {
          if (orderData.products[i].paymentInfo.status === "pending") {
            productIndex = i;
            break;
          }
        }
      }

      // Always use 'stripe' as payment method since it's the only option now
      // Enregistrer également le taux de commission, la commission fixe et la devise
      await FirebaseOrderService.updateProductPaymentInfo(
        currentOrderId,
        productIndex,
        "stripe",
        undefined, // paymentIntentId sera défini plus tard
        "awaiting_transfer", // settlement_status par défaut
        paymentInfo?.commissionRate, // Taux de commission en pourcentage
        paymentInfo?.fixedCommission, // Commission fixe en centimes (EUR)
        paymentInfo?.currency || "eur" // Devise (par défaut "eur")
      );

      // Set the payment method in state
      setPaymentMethod("stripe");

      console.log(`Payment method saved as Stripe for product ${productIndex}`, {
        commissionRate: paymentInfo?.commissionRate,
        fixedCommission: paymentInfo?.fixedCommission,
        currency: paymentInfo?.currency || "eur",
      });
    } catch (_error) {
      console.error("Error saving payment info:", _error);
      toast.error("Failed to save payment method");
      throw _error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeOrder = async () => {
    if (!currentOrderId) {
      toast.error("No order found");
      return;
    }

    setIsLoading(true);
    try {
      console.log("💳 Completing order...");

      // Complete the order (sets status to 'paid')
      await FirebaseOrderService.completeOrder(currentOrderId);

      console.log("✅ Order completed successfully!");
      toast.success(
        "Order completed successfully! Shipping will be created automatically.",
      );

      setCurrentStep("complete");
      setIsOrderComplete(true);
    } catch (_error) {
      console.error("Error completing order:", _error);
      toast.error("Failed to complete order");
      throw _error;
    } finally {
      setIsLoading(false);
    }
  };

  const loadOrder = async (orderId: string) => {
    setIsLoading(true);
    try {
      const order = await FirebaseOrderService.getOrder(orderId);
      if (order) {
        setFirebaseOrder(order);
        setCurrentOrderId(orderId);
        setCurrentStep(order.currentStep);

        // Extract first product for backward compatibility with form
        const firstProduct = order.products?.[0];

        setOrderData({
          firstName: order.orderInfo.firstName,
          lastName: order.orderInfo.lastName,
          email: order.orderInfo.email,
          phone: order.orderInfo.phone,
          tiktokUsername: firstProduct?.tiktokUsername || "",
          productReference: firstProduct?.reference || "",
          productModel: firstProduct?.model || "",
          amount: firstProduct?.amount || "",
          address: order.deliveryInfo.address || "",
          city: order.deliveryInfo.city || "",
          postalCode: order.deliveryInfo.postalCode || "",
          country: order.deliveryInfo.country || "FR",
          // Utiliser methodId si disponible, sinon null
          deliveryMethod: order.deliveryInfo.methodId?.toString() as any || null,
          shippingCost: order.deliveryInfo.shippingCost || 0,
          products: order.products,
        });

        if (order.deliveryInfo.pickupPoint) {
          setSelectedPickupPoint(order.deliveryInfo.pickupPoint);
        }

        // Check if any product has a payment method set
        const hasPaymentMethod = order.products.some(
          (product) => product.paymentInfo.method,
        );
        if (hasPaymentMethod) {
          // Use the first product's payment method for backward compatibility
          const firstPaidProduct = order.products.find(
            (product) => product.paymentInfo.method,
          );
          if (firstPaidProduct?.paymentInfo.method) {
            setPaymentMethod(firstPaidProduct.paymentInfo.method);
          }
        }

        if (order.currentStep === "complete") {
          setIsOrderComplete(true);
        }

        toast.success("Order loaded successfully!");
      } else {
        toast.error("Order not found");
      }
    } catch (_error) {
      console.error("Error loading order:", _error);
      toast.error("Failed to load order");
      throw _error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OrderContext.Provider
      value={{
        orderData,
        setOrderData,
        selectedPickupPoint,
        setSelectedPickupPoint,
        paymentMethod,
        setPaymentMethod,
        isOrderComplete,
        setIsOrderComplete,
        clearOrder,
        currentOrderId,
        currentStep,
        setCurrentStep,
        firebaseOrder,
        isLoading,
        isMergedOrder,
        setIsMergedOrder,
        saveOrder,
        saveDeliveryInfo,
        savePaymentInfo,
        completeOrder,
        loadOrder,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    console.warn("useOrder called outside OrderProvider, returning default values");
    // Retourner des valeurs par défaut au lieu de planter
    return {
      orderData: {} as OrderData,
      setOrderData: () => { },
      selectedPickupPoint: null,
      setSelectedPickupPoint: () => { },
      paymentMethod: null as "stripe" | "paypal" | null,
      setPaymentMethod: () => { },
      isOrderComplete: false,
      setIsOrderComplete: () => { },
      clearOrder: () => { },
      currentOrderId: null,
      currentStep: 1 as const,
      setCurrentStep: () => { },
      firebaseOrder: null,
      isLoading: false,
      isMergedOrder: false,
      setIsMergedOrder: () => { },
      saveOrder: async () => ({ isMerged: false, orderId: null }),
      saveDeliveryInfo: async () => { },
      savePaymentInfo: async () => { },
      completeOrder: async () => { },
      loadOrder: async () => { },
    };
  }
  return context;
};
